import { useState } from "react";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { Colxx } from "../../../../components/common/CustomBootstrap";
import { firestore } from "../../../../helpers/Firebase";
import { createNotification } from "../../../../helpers/Utils";

const ChatLabels = ({ dismiss, selectedLabel }) => {
    const [label, setLabel] = useState(selectedLabel || { name: '', color: '#3b82f6' });
    const [loading, setLoading] = useState(false);
    const [showCustomColor, setShowCustomColor] = useState(false);

    const colors = [
        { value: '#3b82f6', label: 'Blue' },
        { value: '#10b981', label: 'Green' },
        { value: '#f59e0b', label: 'Orange' },
        { value: '#ef4444', label: 'Red' },
        { value: '#8b5cf6', label: 'Purple' },
        { value: '#ec4899', label: 'Pink' },
        { value: '#06b6d4', label: 'Cyan' },
        { value: '#84cc16', label: 'Lime' },
    ];

    const submit = async () => {
        if (!label.name || !label.name.trim()) {
            createNotification('error', 'Please enter a label name', 'Error');
            return;
        }

        setLoading(true);
        try {
            const labelData = {
                name: label.name.trim(),
                color: label?.color || '#3b82f6',
                created: selectedLabel?.created || Date.now(),
                updated: Date.now(),
                active: true
            };

            if (selectedLabel?.id) {
                // Update existing label
                await firestore().collection('chatLabels').doc(selectedLabel.id).update(labelData);
                createNotification('success', 'Label updated successfully', 'Success');
            } else {
                // Create new label
                await firestore().collection('chatLabels').add(labelData);
                createNotification('success', 'Label created successfully', 'Success');
            }
            dismiss();
        } catch (error) {
            console.error("Error saving label:", error);
            createNotification('error', 'Failed to save label', 'Error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            backdrop="static"
            size="md">
            {loading && (
                <div className="loading" />
            )}

            <ModalHeader toggle={dismiss}>
                <div className="d-flex flex-column justify-flex-start" style={{ alignItems: 'flex-start' }}>
                    <h6 className="font-weight-bold m-0 mb-2">
                        {selectedLabel?.id ? 'Edit Label' : 'Create Label'}
                    </h6>
                </div>
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Colxx xxs="12">
                        <Label>Label Name *</Label>
                        <Input
                            type="text"
                            value={label.name}
                            onChange={(e) => setLabel({ ...label, name: e.target.value })}
                            placeholder="Enter label name"
                            maxLength={50}
                        />
                    </Colxx>
                    <Colxx xxs="12" className="mt-3">
                        <Label>Color *</Label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                            {colors.map((color) => (
                                <div
                                    key={color.value}
                                    onClick={() => {
                                        setLabel({ ...label, color: color.value });
                                        setShowCustomColor(false);
                                    }}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        backgroundColor: color.value,
                                        cursor: 'pointer',
                                        border: label.color === color.value && !showCustomColor ? '3px solid #333' : '2px solid #ddd',
                                        boxShadow: label.color === color.value && !showCustomColor ? '0 0 0 2px rgba(0,0,0,0.1)' : 'none',
                                        transition: 'all 0.2s'
                                    }}
                                    title={color.label}
                                />
                            ))}
                            <div
                                onClick={() => setShowCustomColor(true)}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    backgroundColor: showCustomColor ? label.color : '#f3f4f6',
                                    cursor: 'pointer',
                                    border: showCustomColor ? '3px solid #333' : '2px dashed #9ca3af',
                                    boxShadow: showCustomColor ? '0 0 0 2px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                }}
                                title="Custom Color"
                            >
                                {!showCustomColor && (
                                    <span style={{ fontSize: '18px', color: '#6b7280' }}>+</span>
                                )}
                            </div>
                        </div>
                        {showCustomColor && (
                            <div className="mt-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                <Label style={{ margin: 0, fontWeight: 500 }}>Custom Color:</Label>
                                <input
                                    type="color"
                                    value={label.color}
                                    onChange={(e) => setLabel({ ...label, color: e.target.value })}
                                    style={{
                                        width: '50px',
                                        height: '40px',
                                        border: '2px solid #ddd',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <Input
                                    type="text"
                                    value={label.color}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                            setLabel({ ...label, color: value });
                                        }
                                    }}
                                    placeholder="#000000"
                                    style={{
                                        width: '100px',
                                        fontFamily: 'monospace'
                                    }}
                                />
                                <Button
                                    size="sm"
                                    color="secondary"
                                    outline
                                    onClick={() => {
                                        setShowCustomColor(false);
                                        if (!colors.find(c => c.value === label.color)) {
                                            setLabel({ ...label, color: '#3b82f6' });
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        )}
                        <div className="mt-2" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    backgroundColor: label.color,
                                    border: '1px solid #ddd'
                                }}
                            />
                            <span style={{ fontSize: '14px', color: '#6c757d' }}>
                                Selected: {colors.find(c => c.value === label.color)?.label || 'Custom'}
                            </span>
                            {!colors.find(c => c.value === label.color) && (
                                <span style={{ fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>
                                    ({label.color})
                                </span>
                            )}
                        </div>
                    </Colxx>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={dismiss} disabled={loading}>
                    Cancel
                </Button>
                <Button color="primary" onClick={submit} disabled={loading}>
                    {loading ? 'Saving...' : (selectedLabel?.id ? 'Update' : 'Create')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ChatLabels;

